// src/pages/user/InvitationsPage.tsx
// Invite users & manage referral code inside user Settings.

import { useState, useEffect, useCallback } from 'react';
import { observer } from '@react';
import { useUserReferral, useAuth, useI18n } from '@react/hooks';
import {
  Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, ErrorAlert,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '@react/components';
import type { User } from '@core/types/user';

// ─── Inline SVG icons ────────────────────────────────────────────

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

// ─── Copy to clipboard helper ────────────────────────────────────

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);
  return { copied, copy };
}

// ─── Referral Link Card ──────────────────────────────────────────

const ReferralLinkCard = observer(function ReferralLinkCard() {
  const store = useUserReferral();
  const { user } = useAuth();
  const { t } = useI18n();
  const { copied, copy } = useCopyToClipboard();

  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [genError, setGenError] = useState<unknown>(null);

  const userId = user?.entity?.id ?? '';

  const handleGenerate = async () => {
    if (!userId) return;
    setIsGenerating(true);
    setGenError(null);
    try {
      const result = await store.GenerateReferralLink(userId, { user: { id: userId } as never });
      setReferralLink(result.referral_link);
      setReferralCode(result.referral_code);
    } catch (err) {
      setGenError(err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invitations.referralTitle')}</CardTitle>
        <CardDescription>{t('invitations.referralDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {genError != null && <ErrorAlert error={genError} dismissible onDismiss={() => setGenError(null)} />}

        {referralLink ? (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-foreground">{t('invitations.yourLink')}</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm break-all select-all">
                  {referralLink}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copy(referralLink)}
                  aria-label={t('common.copy')}
                >
                  {copied
                    ? <CheckIcon className="h-4 w-4 text-green-600" />
                    : <CopyIcon className="h-4 w-4" />
                  }
                </Button>
              </div>
            </div>
            {referralCode && (
              <div>
                <label className="text-sm font-medium text-foreground">{t('invitations.yourCode')}</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm select-all">
                    {referralCode}
                  </code>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copy(referralCode)}
                    aria-label={t('common.copy')}
                  >
                    {copied
                      ? <CheckIcon className="h-4 w-4 text-green-600" />
                      : <CopyIcon className="h-4 w-4" />
                    }
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-muted/50 border border-border">
            <LinkIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">{t('invitations.referralHint')}</p>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button onClick={handleGenerate} disabled={isGenerating || !userId}>
          <LinkIcon className="h-4 w-4 mr-2" />
          {isGenerating ? t('common.loading') : referralLink ? t('invitations.regenerateLink') : t('invitations.generateLink')}
        </Button>
      </CardFooter>
    </Card>
  );
});

// ─── Invite Form ─────────────────────────────────────────────────

function InviteForm({ onInvited, onCancel }: { onInvited: () => void; onCancel: () => void }) {
  const store = useUserReferral();
  const { user } = useAuth();
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors: Record<string, string> = {};
    if (!email.trim() && !phone.trim()) {
      validationErrors.email = 'invitations.errorEmailOrPhoneRequired';
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      validationErrors.email = 'invitations.errorInvalidEmail';
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.InviteUser({
        inviter: user?.entity ? { id: user.entity.id } as never : undefined,
        message: message.trim(),
        email: email.trim() || undefined,
        phone_number: phone.trim() || undefined,
      });
      onInvited();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <CardHeader>
          <CardTitle>{t('invitations.inviteTitle')}</CardTitle>
          <CardDescription>{t('invitations.inviteDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}

          <Input
            label={t('invitations.fieldEmail')}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.email; return n; }); }}
            placeholder="user@example.com"
            error={errors.email ? t(errors.email) : undefined}
          />

          <Input
            label={t('invitations.fieldPhone')}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 234 567 8900"
          />

          <Input
            label={t('invitations.fieldMessage')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('invitations.fieldMessagePlaceholder')}
          />
        </CardContent>
        <CardFooter className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            <MailIcon className="h-4 w-4 mr-2" />
            {isSubmitting ? t('common.loading') : t('invitations.sendInvite')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

// ─── Invited Users Table ─────────────────────────────────────────

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function statusVariant(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status?.toLowerCase()) {
    case 'active': return 'success';
    case 'pending': return 'warning';
    case 'disabled':
    case 'blocked': return 'error';
    default: return 'default';
  }
}

// ─── Main Page ───────────────────────────────────────────────────

export const InvitationsPage = observer(function InvitationsPage() {
  const store = useUserReferral();
  const { t } = useI18n();

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    store.ListInvitedUsers({
      pagination: { cursor: '', limit: 50, direction: 'forward' },
      sort: [],
      date_range: {} as never,
      filters: {} as never,
    });
  }, []);

  const handleInvited = () => {
    setShowInviteForm(false);
    setSuccessMessage(t('invitations.inviteSent'));
    setTimeout(() => setSuccessMessage(''), 5000);
    store.ListInvitedUsers({
      pagination: { cursor: '', limit: 50, direction: 'forward' },
      sort: [],
      date_range: {} as never,
      filters: {} as never,
    });
  };

  const invitedUsers = store.items as User[];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('invitations.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('invitations.subtitle')}</p>
        </div>
        {!showInviteForm && (
          <Button onClick={() => { setShowInviteForm(true); setSuccessMessage(''); }}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('invitations.inviteUser')}
          </Button>
        )}
      </div>

      {/* Error */}
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Success */}
      {successMessage && (
        <Alert variant="success" dismissible onDismiss={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Referral link card */}
      <ReferralLinkCard />

      {/* Invite form */}
      {showInviteForm && (
        <InviteForm onInvited={handleInvited} onCancel={() => setShowInviteForm(false)} />
      )}

      {/* Invited users table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('invitations.invitedUsersTitle')}</CardTitle>
          <CardDescription>{t('invitations.invitedUsersDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('invitations.columnUser')}</TableHead>
                <TableHead>{t('invitations.columnEmail')}</TableHead>
                <TableHead>{t('invitations.columnStatus')}</TableHead>
                <TableHead>{t('invitations.columnInvitedAt')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {store.isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <div className="text-muted-foreground">{t('common.loading')}</div>
                  </TableCell>
                </TableRow>
              ) : invitedUsers.length === 0 ? (
                <TableEmpty colSpan={4} message={t('invitations.emptyState')} />
              ) : (
                invitedUsers.map((u) => (
                  <TableRow key={u.entity?.id}>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {u.username || u.email || u.phone || t('invitations.unknownUser')}
                        </p>
                        {u.username && u.email && (
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(u.status ?? '')}>{u.status || 'pending'}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(u.created_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default InvitationsPage;
